/*
 * Every filter the components endpoint accepts, all blank. Blank values are stripped before the
 * query string is built, so an unset filter is absent rather than sent as an empty value.
 */
export const EMPTY_FLEET_FILTERS = {
	category: '',
	manufacturer: '',
	model: '',
	firmware: '',
	serialNumber: ''
};

export const GET_FLEET_COMPONENTS_DEFAULT_QUERY_PARAMS = {
	start: 0,
	limit: 15,
	...EMPTY_FLEET_FILTERS
};

/*
 * The fleet inventory endpoint caps `limit` at 200 and rejects anything above it with a 422, so
 * the page size list must not offer more than that.
 */
export const FLEET_COMPONENTS_MAX_PAGE_SIZE = 200;

export const FLEET_COMPONENTS_SIZE_PER_PAGE_LIST = [15, 30, 60, 100, FLEET_COMPONENTS_MAX_PAGE_SIZE];

/*
 * How many pages the firmware advisory form walks when it samples the fleet for the firmware
 * versions a component type is running. There is no endpoint that returns the distinct versions,
 * so the only way to build that list is to page the component rows and count them.
 *
 * Ten pages of 200 is 2000 components. This runs while someone waits to pick a version, and the
 * walk is sequential because page one's `totalItems` is what says how many pages exist, so the cap
 * is a latency budget rather than a data one. Past it the list is partial and says so.
 */
export const FIRMWARE_VERSION_SCAN_MAX_PAGES = 10;

/*
 * The page splits its two tables across tabs rather than stacking them: the analytics catalog is
 * unpaginated and long enough that a stacked fleet table falls below the fold.
 */
export const HARDWARE_INVENTORY_TABS = {
	COMPONENT_TYPES: 'componentTypes',
	COMPONENTS: 'components'
};

export const HARDWARE_INVENTORY_ACTIVE_TAB_KEY = 'hardwareInventoryActiveTab';

/*
 * The component type analytics endpoint is unpaginated, so the table pages client-side purely to
 * keep the page a predictable height - the fleet inventory below it has to stay reachable without
 * scrolling past an arbitrarily long catalog.
 */
export const COMPONENT_TYPES_PAGE_SIZE = 25;

export const COMPONENT_TYPES_SIZE_PER_PAGE_LIST = [25, 50, 100, 200];

/*
 * The approval-status filter over the component type catalog. Blank is "no filter", matching the
 * convention the fleet filters use, so the unset state is the whole catalog rather than a subset.
 *
 * Filtering is client-side: the analytics endpoint is unpaginated and takes no filter parameters,
 * so there is nothing to ask the server for.
 */
export const APPROVAL_STATUS_FILTER = {
	ALL: '',
	APPROVED: 'approved',
	NOT_APPROVED: 'notApproved'
};

/*
 * The component categories the API accepts for its `category` filter. Anything else is a 422, so
 * these are only ever used for display and for building filters out of analytics rows.
 */
export const COMPONENT_CATEGORIES = [
	'cpu',
	'gpu',
	'nic',
	'drive',
	'controller',
	'psu',
	'bios',
	'bmc',
	'motherboard',
	'chassis',
	'tpm',
	'ram',
	'enclosure'
];

export const NOT_AVAILABLE = 'N/A';

/*
 * The API's verdict on one component's firmware against its component type's approved version.
 * Ranked server-side with a version-aware comparison the UI cannot reproduce - the same ordering
 * behind `oldestFirmwareVersion` - because comparing these as strings gets `10.0` against `9.0`
 * wrong.
 *
 * Absent when either side is missing: the component reported no firmware, or nobody has approved a
 * version for its type yet. Both are ordinary states rather than errors.
 */
export const FIRMWARE_COMPARISON = {
	BEHIND: 'behind',
	AHEAD: 'ahead',
	MATCH: 'match'
};

export const APPROVAL_FORM_NAME = 'componentTypeApproval';

/*
 * The approval write returns a job id rather than the updated component type, so it is followed by
 * a poll. Sixty seconds is the same budget `pollJobUntilTerminal` defaults to, named here for the
 * same reason the advisory and override features name theirs: so the modal cannot drift from
 * whatever else comes to write approvals later.
 */
export const COMPONENT_TYPE_JOB_POLL = {
	intervalMs: 1000,
	maxAttempts: 60
};

export const EMPTY_APPROVAL_FORM_VALUES = {
	approvedFirmwareVersion: ''
};
